import React, { type ReactNode, useMemo } from 'react';
import { App, ConfigProvider } from 'antd';
import { useIntl } from 'react-intl';
import type { Locale } from 'antd/lib/locale';
import antdLocaleUS from 'antd/locale/en_US';
import { getAntdTheme } from 'constants/antd.constants';
import { observer } from 'mobx-react';
import { useTheme } from 'hooks/use-theme';
import { css, Global } from '@emotion/react';
import type { Env0Theme } from 'constants/themes.constants';
import { IconProvider } from '@ant-design/icons';
import { BridgedEmpty, CompactEmpty } from 'components/common/bridged-empty';
import { TableEmptyState } from 'components/common/table/table-empty-state';

/**
 * This component is used to wrap the entire application.
 * The ConfigProvider sets the base tokens and the locales (translations) for the antd components.
 * The App component is used to set the base styles for the application (i.e. root font-size, root color, etc.)
 */

/**
 * antd 5.6.4 portals every popup to document.body, so a Select inside a Popover is ranked against it
 * by raw z-index instead of nesting inside it. The DS ladder puts dropdown below drawer and modal,
 * which holds only when a nested popup escapes that order through its container's stacking context.
 * Portalling into the container restores it. antd 5.11's zIndexContext replaces this — APO-462.
 */
const NESTED_POPUP_CONTAINERS = '.ant-modal-content, .ant-drawer-body, .ant-popover-inner';

export const getNestedPopupContainer = (triggerNode?: HTMLElement) =>
  triggerNode?.closest<HTMLElement>(NESTED_POPUP_CONTAINERS) ?? document.body;

// Legacy keeps antd's flat portalling: its ladder ranks the dropdown above the popover already.
export const getPopupContainerForTheme = (theme: Env0Theme) =>
  theme.isNewDesign ? getNestedPopupContainer : undefined;

// antd's defaultRenderEmpty keeps these compact: they render inside a popup.
const COMPACT_EMPTY_COMPONENTS = ['Select', 'TreeSelect', 'Cascader', 'Transfer', 'Mentions'];

// `Table`/`List` arrive here whenever a call site passes neither `emptyMessageId` nor `emptyComponent`
// — `table.tsx` leaves `locale` empty and antd falls through to `renderEmpty('Table')`. They get the
// same composition the propped case wires, so there is only ever one in-table empty state.
export const renderDesignSystemEmpty = (componentName?: string) => {
  if (componentName && COMPACT_EMPTY_COMPONENTS.includes(componentName)) {
    return <CompactEmpty />;
  }

  return componentName === 'Table' || componentName === 'List' ? <TableEmptyState type="no-data" /> : <BridgedEmpty />;
};

// Undefined with the switch off, which is what the app does today.
export const getRenderEmptyForTheme = (theme: Env0Theme) => (theme.isNewDesign ? renderDesignSystemEmpty : undefined);

const AntdWrapper: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const intl = useIntl();
  const { appTheme } = useTheme();

  const theme = useMemo(() => getAntdTheme(appTheme), [appTheme]);

  const locale = useMemo<Locale>(
    () => ({
      ...antdLocaleUS,
      Table: {
        ...antdLocaleUS.Table,
        triggerAsc: intl.formatMessage({ id: 'sort.ascending' }),
        triggerDesc: intl.formatMessage({ id: 'sort.descending' }),
        cancelSort: intl.formatMessage({ id: 'sort.cancel' })
      }
    }),
    [intl]
  );
  return (
    <ConfigProvider
      csp={{ nonce: window.nonce }}
      theme={theme}
      locale={locale}
      getPopupContainer={getPopupContainerForTheme(appTheme)}
      renderEmpty={getRenderEmptyForTheme(appTheme)}>
      <IconProvider value={{ csp: { nonce: window.nonce } }}>
        <Global styles={getGlobalStyles(appTheme)} />
        <App>{children}</App>
      </IconProvider>
    </ConfigProvider>
  );
};

export default observer(AntdWrapper);

export const getGlobalStyles = (theme: Env0Theme) =>
  theme.isNewDesign ? newDesignGlobalStyles(theme) : legacyGlobalStyles(theme);

// Verbatim pre-ATH-356, and it has to stay that way: `__tests__/components/__fixtures__/legacy-global-styles.*.css`
// byte-compares this function's output against master's, so factoring the shared rules out would fail CI.
// APO-484 deletes it whole at the flip.
const legacyGlobalStyles = (theme: Env0Theme) => css`
  * {
    scrollbar-width: thin;
    scrollbar-color: ${theme.scrollbarThumbBackground} ${theme.scrollbarTrackBackground};
  }

  *::-webkit-scrollbar {
    width: 10px;
  }

  *::-webkit-scrollbar-thumb {
    background-color: ${theme.scrollbarThumbBackground};
    border-radius: 12px;
  }

  *::-webkit-scrollbar-track {
    background-color: ${theme.scrollbarTrackBackground};
  }
  .ant-collapse-content-box {
    // Necessary because in v5, the content padding is a constant hard-written in antd
    // (https://github.com/ant-design/ant-design/blob/master/components/collapse/style/index.ts#L301)
    padding: 0 !important;
  }

  .ant-dropdown-menu {
    // Necessary because v4 used to have this property, but v5 doesn't
    // (https://github.com/ant-design/ant-design/blob/4.x-stable/components/dropdown/style/index.less#L122)
    text-align: left;
  }

  .ant-modal {
    // Necessary because v4 used to have dividers between header / content / footer, but v5 doesn't
    // thus the spacing is just too compact and needs to be increased (not configurable via tokens)

    &-header {
      margin-bottom: 32px !important;
    }

    &-footer {
      margin-top: 32px !important;
    }
  }
  .ant-drawer {
    &-header-title {
      flex-direction: row-reverse;
    }

    &-close {
      margin-inline-end: 0;
      margin-inline-start: 12px;
    }
  }

  .ant-select-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background-color: ${theme.primaryGray}; /* or any theme color */
  }

  // AntD 5.6.4 doesn’t expose a defaultHoverBg token in the source. To change default-button hover background, use a CSS override
  // https://github.com/ant-design/ant-design/blob/5.6.4/components/button/style/index.ts
  /* Default button hover */

  /* Scope overrides to DEFAULT button only */
  .ant-btn-default {
    /* Optional: keep AntD transitions explicitly (AntD sets them, this just guards) */
    transition:
      color 0.2s,
      border-color 0.2s,
      background-color 0.2s,
      box-shadow 0.2s;

    /* Optional: ensure wave color matches your scheme for default buttons only */
    --antd-wave-shadow-color: ${theme.primaryBlack};
  }

  /* Hover: change only color/border; don't touch box-shadow */
  .ant-btn-default:not(:disabled):hover {
    background-color: transparent !important;
    border-color: ${theme.primaryBlack} !important;
    color: ${theme.primaryBlack} !important;
  }

  /* Make active visually distinct from hover for DEFAULT buttons */
  .ant-btn-default:not(:disabled):active {
    /* keep transitions and wave; only adjust appearance */
    color: ${theme.primaryBlack} !important;
    border-color: ${theme.primaryBlack} !important;

    /* subtle pressed feedback — adjust to taste */
    background-color: rgba(0, 0, 0, 0.04) !important; /* tiny tint */
  }

  /* Ensure inner text/icons adopt the color */
  .ant-btn-default:not(:disabled):hover > span,
  .ant-btn-default:not(:disabled):hover .anticon,
  .ant-btn-default:not(:disabled):hover svg,
  .ant-btn-default:not(:disabled):active > span,
  .ant-btn-default:not(:disabled):active .anticon,
  .ant-btn-default:not(:disabled):active svg {
    color: ${theme.primaryBlack} !important;
    fill: ${theme.primaryBlack} !important;
  }

  /* Focus: keep AntD focus ring (box-shadow). Don’t null it out. */
  .ant-btn-default:focus-visible {
    outline: none; /* AntD uses box-shadow, not outline */
    box-shadow: inherit; /* preserve AntD focus shadow */
  }

  /* Active filter icon color in AntD Table */
  .ant-table-column-sorter-up.active,
  .ant-table-column-sorter-down.active,
  .ant-table-filter-trigger.active,
  .ant-table-filter-trigger.active .anticon,
  .ant-table-filter-trigger.active svg {
    color: ${theme.limeYellowActive} !important;
    fill: ${theme.limeYellowActive} !important;
  }

  /* Remove box-shadow from AntD dangerous buttons */
  .ant-btn-dangerous,
  .ant-btn-dangerous:hover,
  .ant-btn-dangerous:active,
  .ant-btn-dangerous:focus,
  .ant-btn-dangerous:focus-visible {
    box-shadow: none !important;
    background-color: ${theme.red} !important;
  }

  .ant-tooltip-inner {
    a {
      color: ${theme.tooltipLink} !important;
    }
  }
`;

const newDesignGlobalStyles = (theme: Env0Theme) => css`
  * {
    scrollbar-width: thin;
    scrollbar-color: ${theme.scrollbarThumbBackground} ${theme.scrollbarTrackBackground};
  }

  *::-webkit-scrollbar {
    width: 10px;
  }

  *::-webkit-scrollbar-thumb {
    background-color: ${theme.scrollbarThumbBackground};
    border-radius: 12px;
  }

  *::-webkit-scrollbar-track {
    background-color: ${theme.scrollbarTrackBackground};
  }

  // Content and header padding share Collapse's \`padding\` token, so the theme config cannot zero one
  // without the other. !important because antd's own selector is the more specific.
  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;
